/**
 * Learning Tree Service
 */

import { httpClient } from '../http-client';
import type { KnowledgeTreeResponse, UserLearningTree, TreeType, LearningType } from './types';
import type { ProblemSpace } from '@careertree/shared';

// Re-export for convenience
export type { ProblemSpace } from '@careertree/shared';

const API_BASE_URL = '/api/v1';

export const LearningTreeService = {
  /**
   */
  async getExistingKnowledgeTree(treeId: string): Promise<KnowledgeTreeResponse> {
    try {
      const url = `${API_BASE_URL}/learning-trees/${treeId}`;
      const response = await httpClient.get<KnowledgeTreeResponse>(url);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch existing knowledge tree:', error);
      throw error;
    }
  },

  /**
   */
  async getKnowledgeTree(
    topic: string,
    topicContext: string[] = [],
    language: string = 'ko',
    treeType: TreeType = 'sequence'
  ): Promise<KnowledgeTreeResponse> {
    try {
      const url = `${API_BASE_URL}/learning-trees`;

      const requestBody = {
        topic,
        topicContext,
        language,
        treeType
      };

      const response = await httpClient.post<KnowledgeTreeResponse>(url, requestBody);
      return response.data;
    } catch (error) {
      console.error('Error fetching knowledge tree:', error);
      throw error;
    }
  },

  /**
   */
  async createLearningTreeWithAnswers(
    topicContext: string[],
    topic: string,
    language: string = 'ko',
    treeType: TreeType,
    learningType: LearningType = 'Apply',
    userCustom: boolean = false,
    category?: string,
    problemSpace?: ProblemSpace
  ): Promise<{ learningTreeId: string; workflowId: string; status: string }> {
    const url = `${API_BASE_URL}/learning-trees`;

    const response = await httpClient.post<{ data: { learningTreeId: string; workflowId: string; status: string } }>(url, {
      topicContext,
      topic,
      language,
      treeType,
      learningType,
      userCustom,
      category,
      problemSpace,
    });

    return response.data.data;
  },

  /**
   * @deprecated Use createLearningTreeWithAnswers instead
   */
  async sendLearningTreeSignal(
    workflowId: string,
    topicContext: string[],
    topic: string,
    language: string = 'ko',
    treeType: TreeType,
    learningType: LearningType = 'Apply',
    userCustom: boolean = false
  ): Promise<{ learningTreeId: string; workflowId: string; status: string }> {
    console.warn('[LearningTreeService] sendLearningTreeSignal is deprecated. Use createLearningTreeWithAnswers instead.');
    return this.createLearningTreeWithAnswers(
      topicContext,
      topic,
      language,
      treeType,
      learningType,
      userCustom
    );
  },

  /**
   * @deprecated Use createLearningTreeWithAnswers instead
   */
  async createLearningTree(
    topic: string,
    topicContext: string[] = [],
    treeType: TreeType = 'sequence',
    language: string = 'ko',
  ): Promise<{ id: string; status: string }> {
    const url = `${API_BASE_URL}/learning-trees`;

    const response = await httpClient.post<{ data: { id: string; status: string } }>(url, {
      topic,
      topicContext,
      treeType,
      language,
    });

    return response.data.data;
  },

  /**
   */
  async getLearningTree(id: string): Promise<KnowledgeTreeResponse> {
    const url = `${API_BASE_URL}/learning-trees/${id}`;
    const response = await httpClient.get<KnowledgeTreeResponse>(url);
    return response.data;
  },

  /**
   */
  async getGenerationStatus(treeId: string): Promise<{
    id: string;
    status: string;
    completedSteps: string[];
  }> {
    try {
      const url = `${API_BASE_URL}/learning-trees/${treeId}/generation-status`;
      const response = await httpClient.get<{ id: string; status: string; completedSteps: string[] }>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching generation status:', error);
      throw error;
    }
  },

  /**
   * Get user's learning trees
   */
  async getUserLearningTrees(learningType?: string): Promise<UserLearningTree[]> {
    try {
      let url = `${API_BASE_URL}/learning-trees/user`;
      if (learningType) {
        url += `?learningType=${learningType}`;
      }
      const response = await httpClient.get<UserLearningTree[]>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching user learning trees:', error);
      throw error;
    }
  },

  /**
   */
  async getUserLearningTreeCounts(): Promise<{
    Understanding: number;
    Apply: number;
    Analyze: number;
    total: number;
  }> {
    try {
      const url = `${API_BASE_URL}/learning-trees/user/count`;
      const response = await httpClient.get<{ Understanding: number; Apply: number; Analyze: number; total: number }>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching learning tree counts:', error);
      throw error;
    }
  },

  /**
   */
  async searchLearningTrees(query: string): Promise<UserLearningTree[]> {
    try {
      const url = `${API_BASE_URL}/learning-trees/search`;
      const response = await httpClient.get<UserLearningTree[]>(url, {
        params: { q: query }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching learning trees:', error);
      throw error;
    }
  },
};
